import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, effect, EventEmitter, inject, input, Output, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { CalendarModule } from 'primeng/calendar';
import { DialogService } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { ButtonDirective } from '../../directives/button.directive';
import { BaseWorkPackageUserDto } from '@api/backend-connector/models';
import { AssigneePickerComponent } from 'src/app/components-form/assignee-picker/assignee-picker.component';
import { UserAvatarPipe } from 'src/app/pipes/user-avatar.pipe copy';
import { UserFullNamePipe } from 'src/app/pipes/user-full-name.pipe';
import { MenuItemComponent } from 'src/app/widgets/menu/menu-item/menu-item.component';
import { MenuComponent } from 'src/app/widgets/menu/menu.component';

const getIds = (assignees: (BaseWorkPackageUserDto | null)[]) =>
  assignees.map((c) => c?.id).filter((id) => !!id) as string[];

@Component({
  selector: 'app-wp-assignees',
  templateUrl: './wp-assignees.component.html',
  styleUrls: ['./wp-assignees.component.scss'],
  standalone: true,
  imports: [
    AssigneePickerComponent,
    AvatarModule,
    ButtonDirective,
    CalendarModule,
    FormsModule,
    InputNumberModule,
    NgClass,
    MenuComponent,
    MenuItemComponent,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    SkeletonModule,
    TooltipModule,
    TranslateModule,
    UserAvatarPipe,
    UserFullNamePipe,
  ],
})
export class WpAssigneesComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly dialog = inject(DialogService);

  readonly control = input.required<FormArray<FormControl<BaseWorkPackageUserDto>>>();
  readonly title = input.required<string>();
  readonly projectId = input.required<string>();
  readonly addPlaceholder = input.required<string>();
  readonly noItemsLabel = input.required<string>();
  readonly showListOnly = input<boolean>(false);

  @Output() readonly addGroupMembers = new EventEmitter<void>();

  readonly currentIds = signal<string[]>([]);

  constructor() {
    effect(
      () => {
        const formArray = this.control();
        this.currentIds.set(getIds(this.control().value));
        const sub = formArray.valueChanges.subscribe((value) => {
          this.currentIds.set(getIds(value));
        });
        return () => sub.unsubscribe();
      },
      { allowSignalWrites: true },
    );
  }

  get canManage() {
    return this.control().enabled;
  }

  hasItems() {
    return this.control().controls.length > 0;
  }

  isAdding() {
    return this.control().controls.find((c) => !c.value);
  }

  canAdd() {
    return !this.isAdding();
  }

  abortAdd() {
    this.control().removeAt(0);
  }

  addAssignee() {
    const c = this.fb.nonNullable.control(null as unknown as BaseWorkPackageUserDto);
    this.control().insert(0, c);
    this.control().markAsUntouched();
  }

  clearAssignees() {
    this.control().clear();
    this.control().markAsDirty();
    this.cdr.detectChanges();
  }

  async remove(index: number) {
    this.control().removeAt(index);
    this.control().markAsDirty();
    this.cdr.detectChanges();
  }
}
